import React, { useState, useEffect, useContext } from 'react';
import { firestore } from '../../firebase/config';
import { BookingContext } from '../../context/BookingContext';
import './Calendar.css';

const Calendar = ({ practitionerId, serviceId, serviceDuration, onDateSelect }) => {
  const { setCurrentStep, updateBookingData, bookingData } = useContext(BookingContext);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedDay, setSelectedDay] = useState(null);
  const [calendarDays, setCalendarDays] = useState([]);
  const [unavailableDays, setUnavailableDays] = useState({});
  const [vacationDates, setVacationDates] = useState([]);
  const [daysWithoutSlots, setDaysWithoutSlots] = useState({});
  const [remoteOnlyDays, setRemoteOnlyDays] = useState({}); // Jours où seuls les RDV à distance sont possibles
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Nouvel état pour le choix du type de rendez-vous
  const [isRemoteAppointment, setIsRemoteAppointment] = useState(
    bookingData.isRemote !== undefined ? bookingData.isRemote : false
  );

  // Mois en français
  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  // Jours de la semaine en français
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  // Exposer la fonction de mise à jour des jours sans créneaux au niveau global
  // pour que le composant TimeSlots puisse y accéder
  useEffect(() => {
    window.updateDaysWithoutSlots = (dateStr, hasNoSlots) => {
      console.log(`Mise à jour des jours sans créneaux: ${dateStr} -> ${hasNoSlots}`);
      setDaysWithoutSlots(prev => ({
        ...prev,
        [dateStr]: hasNoSlots
      }));
    };

    // Nettoyage à la destruction du composant
    return () => {
      delete window.updateDaysWithoutSlots;
    };
  }, []);
  
  // Initialiser la valeur de isRemoteAppointment d'après le contexte
  useEffect(() => {
    // Si le service est uniquement à distance, forcer la consultation à distance
    if (bookingData.remoteOnly) {
      setIsRemoteAppointment(true);
    } else if (bookingData.isRemote !== undefined) {
      setIsRemoteAppointment(bookingData.isRemote);
    }
  }, [bookingData.isRemote, bookingData.remoteOnly]);

  // Charger les disponibilités du praticien
  useEffect(() => {
    const loadCalendarData = async () => {
      if (!practitionerId) {
        setError("Aucun praticien sélectionné.");
        setLoading(false);
        return;
      }

      if (!serviceId) {
        setError("Aucun service sélectionné.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Générer les jours du mois
        generateCalendarDays();

        // Récupérer les disponibilités par défaut du praticien
        const defaultAvailabilitiesSnapshot = await firestore
          .collection('availability')
          .where('practitionerId', '==', practitionerId)
          .where('type', '==', 'default')
          .get();

        console.log("Disponibilités par défaut récupérées:", defaultAvailabilitiesSnapshot.size);

        // Créer un objet avec les jours de la semaine où le praticien n'est pas disponible
        const unavailableDaysOfWeek = {};
        const remoteOnlyDaysOfWeek = {}; // Jours où seuls les RDV à distance sont possibles

        if (defaultAvailabilitiesSnapshot.empty) {
          // Pas de disponibilités définies, on considère les weekends comme non disponibles par défaut
          unavailableDaysOfWeek[5] = true; // Samedi (index 5 dans notre système)
          unavailableDaysOfWeek[6] = true; // Dimanche (index 6 dans notre système)
          console.log("Aucune disponibilité trouvée, weekends considérés fermés par défaut");
        } else {
          // Parcourir les disponibilités
          defaultAvailabilitiesSnapshot.docs.forEach(doc => {
            const data = doc.data();
            console.log("Disponibilité pour le jour", data.day, ":", data);

            // Vérifier explicitement que isWorkingDay est false
            if (data.isWorkingDay === false) {
              unavailableDaysOfWeek[data.day] = true; // 0=lundi, 1=mardi, etc.
              console.log("Jour marqué comme non travaillé:", data.day);
            }
            // Vérifier si c'est un jour uniquement à distance
            else if (data.remoteOnly === true) {
              console.log("Jour marqué comme uniquement à distance:", data.day);

              // Si le service sélectionné ne peut pas être à distance et que c'est un jour uniquement à distance,
              // marquer ce jour comme non disponible
              if (bookingData.isRemote === false) {
                unavailableDaysOfWeek[data.day] = true;
                console.log("Jour marqué comme non disponible pour ce service (présentiel uniquement):", data.day);
              }
            }
            // Vérifier si le jour n'autorise pas les rendez-vous à distance alors que le service est uniquement à distance
            else if (data.allowsRemote === false && bookingData.isRemote === true) {
              unavailableDaysOfWeek[data.day] = true;
              console.log("Jour marqué comme non disponible pour ce service (à distance uniquement):", data.day);
            }
          });
        }

        // Récupérer les absences pour un intervalle étendu (mois précédent + mois actuel + mois suivant)
        const firstDayPrevMonth = new Date(currentYear, currentMonth - 1, 1);
        const lastDayNextMonth = new Date(currentYear, currentMonth + 2, 0);

        const vacationsSnapshot = await firestore
          .collection('vacations')
          .where('practitionerId', '==', practitionerId)
          .where('isFullDay', '==', true)
          .get();

        const vacationDatesList = [];

        // Processus de recueil des dates d'absences
        vacationsSnapshot.docs.forEach(doc => {
          const vacation = doc.data();
          const startDate = new Date(vacation.startDate);
          const endDate = new Date(vacation.endDate);

          // Si les dates d'absences chevauchent la période étendue
          if (startDate <= lastDayNextMonth && endDate >= firstDayPrevMonth) {
            // Pour chaque jour entre la date de début et la date de fin
            const currentDate = new Date(Math.max(startDate, firstDayPrevMonth));
            const effectiveEndDate = new Date(Math.min(endDate, lastDayNextMonth));

            while (currentDate <= effectiveEndDate) {
              vacationDatesList.push(currentDate.toISOString().split('T')[0]);
              currentDate.setDate(currentDate.getDate() + 1);
            }
          }
        });

        console.log("Dates d'absences récupérées:", vacationDatesList);
        setVacationDates(vacationDatesList);

        // Mettre à jour l'état avec les jours non disponibles
        setUnavailableDays({
          daysOfWeek: unavailableDaysOfWeek,
          specificDates: {} // Sera rempli plus tard si nécessaire
        });

        // Mettre à jour l'état avec les jours uniquement à distance
        setRemoteOnlyDays(remoteOnlyDaysOfWeek);

        console.log("Jours non disponibles:", unavailableDaysOfWeek);
        console.log("Jours uniquement à distance:", remoteOnlyDaysOfWeek);

        setLoading(false);
      } catch (error) {
        console.error("Erreur lors du chargement du calendrier:", error);
        setError("Impossible de charger les disponibilités du praticien.");
        setLoading(false);
      }
    };

    loadCalendarData();
  }, [currentMonth, currentYear, practitionerId, serviceId, serviceDuration, bookingData.canBeRemote, bookingData.isRemote, bookingData.remoteOnly]);

  // Générer les jours pour le calendrier
  const generateCalendarDays = () => {
    // Premier jour du mois actuel
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);

    // Jour de la semaine du premier jour (0 = dimanche, 1 = lundi, ...)
    let firstDayOfWeek = firstDayOfMonth.getDay();
    // Ajuster pour que la semaine commence le lundi (0 = lundi, 6 = dimanche)
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    // Nombre de jours dans le mois actuel
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

    // Jours du mois précédent pour compléter la première semaine
    const daysFromPrevMonth = firstDayOfWeek;

    // Tableau pour stocker tous les jours à afficher
    const days = [];

    // Ajouter les jours du mois précédent
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const daysInPrevMonth = new Date(prevMonthYear, prevMonth + 1, 0).getDate();

    for (let i = 0; i < daysFromPrevMonth; i++) {
      const day = daysInPrevMonth - daysFromPrevMonth + i + 1;

      // Créer la date pour vérifier si elle est dans le passé
      const date = new Date(prevMonthYear, prevMonth, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const isPast = date < today;

      // Obtenir le jour de la semaine
      const dayOfWeek = date.getDay();
      // Convertir pour format lundi=0, dimanche=6
      const adjustedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

      days.push({
        day,
        month: prevMonth,
        year: prevMonthYear,
        currentMonth: false,
        isPast,
        dayOfWeek: adjustedDayOfWeek,
        tooltip: isPast ? "Date passée" : "Mois précédent",
        disabled: isPast // Seulement désactiver si dans le passé
      });
    }

    // Ajouter les jours du mois actuel
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentYear, currentMonth, i);
      const isPast = date < today;

      // Obtenir le jour de la semaine selon notre format (0=lundi, 6=dimanche)
      const dayOfWeek = date.getDay();
      // Convertir pour format lundi=0, dimanche=6
      const adjustedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

      days.push({
        day: i,
        month: currentMonth,
        year: currentYear,
        currentMonth: true,
        isPast,
        dayOfWeek: adjustedDayOfWeek,
        tooltip: isPast ? "Date passée" : null,
        disabled: isPast
      });
    }

    // Ajouter les jours du mois suivant pour compléter la dernière semaine
    const totalDaysAdded = days.length;
    const daysFromNextMonth = Math.ceil(totalDaysAdded / 7) * 7 - totalDaysAdded;

    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextMonthYear = currentMonth === 11 ? currentYear + 1 : currentYear;

    for (let i = 1; i <= daysFromNextMonth; i++) {
      // Créer la date pour les vérifications
      const date = new Date(nextMonthYear, nextMonth, i);
      const isPast = date < today;

      // Obtenir le jour de la semaine
      const dayOfWeek = date.getDay();
      // Convertir pour format lundi=0, dimanche=6
      const adjustedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

      days.push({
        day: i,
        month: nextMonth,
        year: nextMonthYear,
        currentMonth: false,
        isPast,
        dayOfWeek: adjustedDayOfWeek,
        tooltip: isPast ? "Date passée" : "Mois suivant",
        disabled: isPast // Seulement désactiver si dans le passé
      });
    }

    setCalendarDays(days);
  };

  // Passer au mois précédent
  const prevMonth = () => {
    setSelectedDay(null);
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  // Passer au mois suivant
  const nextMonth = () => {
    setSelectedDay(null);
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Vérifier si une date est en congé
  const isVacationDay = (day) => {
    // Formater la date en YYYY-MM-DD en conservant le fuseau horaire local
    const year = day.year;
    const month = String(day.month + 1).padStart(2, '0');
    const date = String(day.day).padStart(2, '0');
    const dateStr = `${year}-${month}-${date}`;

    return vacationDates.includes(dateStr);
  };

  // Vérifier si le praticien n'est pas disponible à cette date
  const isUnavailableDay = (day) => {
    // Vérifier d'abord si c'est une date spécifique non disponible
    const year = day.year;
    const month = String(day.month + 1).padStart(2, '0');
    const date = String(day.day).padStart(2, '0');
    const dateStr = `${year}-${month}-${date}`;

    if (unavailableDays.specificDates && unavailableDays.specificDates[dateStr]) {
      return true;
    }

    // Vérifier si c'est un jour de la semaine non travaillé
    const isDayOff = unavailableDays.daysOfWeek && unavailableDays.daysOfWeek[day.dayOfWeek];

    return isDayOff;
  };

  // Vérifier si une date n'a pas de créneaux disponibles
  const hasNoTimeSlots = (day) => {
    const year = day.year;
    const month = String(day.month + 1).padStart(2, '0');
    const date = String(day.day).padStart(2, '0');
    const dateStr = `${year}-${month}-${date}`;

    // Vérifier dans l'état local si ce jour a été marqué comme n'ayant pas de créneaux
    return daysWithoutSlots[dateStr];
  };

  // Vérifier si cette date est uniquement pour les rendez-vous à distance
  const isRemoteOnlyDay = (day) => {
    return remoteOnlyDays[day.dayOfWeek];
  };

  // Vérifier si un jour est réservable ou non
  const isDayBookable = (day) => {
    // Si le jour est dans le passé, il n'est pas réservable
    if (day.disabled || day.isPast) {
      return false;
    }

    // Vérifier les autres conditions de non-disponibilité
    if (isVacationDay(day) || isUnavailableDay(day) || hasNoTimeSlots(day)) {
      return false;
    }

    return true;
  };

  // Sélectionner un jour
  const selectDay = (day) => {
    // Ne pas permettre la sélection des jours non réservables
    if (!isDayBookable(day)) {
      console.log("Jour non sélectionnable:", day);
      return;
    }

    // IMPORTANT: Création d'une date à midi (12:00) en heure locale
    // pour éviter tout problème de fuseau horaire
    const selectedDate = new Date(day.year, day.month, day.day, 12, 0, 0);

    console.log("Date sélectionnée:", selectedDate);
    console.log("Date formatée pour debug:",
      `${selectedDate.getFullYear()}-${selectedDate.getMonth() + 1}-${selectedDate.getDate()}`);

    setSelectedDay(day);

    // Vérifier si c'est un jour uniquement à distance et mettre à jour le contexte
    const isRemoteOnly = isRemoteOnlyDay(day);
    if (isRemoteOnly && !bookingData.remoteOnly) {
      updateBookingData({
        isRemote: true,
        selectedDateRemoteOnly: true
      });
      console.log("Jour uniquement à distance, mise à jour du contexte");
    } else if (!isRemoteOnly && bookingData.selectedDateRemoteOnly) {
      // Si précédemment on avait sélectionné un jour uniquement à distance
      // et maintenant ce n'est plus le cas, réinitialiser selectedDateRemoteOnly
      updateBookingData({
        selectedDateRemoteOnly: false,
        // Ne pas changer isRemote car l'utilisateur peut avoir choisi de son côté
      });
    }

    // Notifier le composant parent
    if (onDateSelect) {
      onDateSelect(selectedDate);
    }

    // Mettre à jour la date formatée pour l'affichage
    const formattedDate = selectedDate.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    updateBookingData({ 
      formattedDate,
      isRemote: isRemoteAppointment // Mettre à jour avec le choix fait sur cette page
    });

    // Passer à l'étape suivante
    setCurrentStep(4);
  };

  // Gérer le changement d'option de rendez-vous à distance
  const handleRemoteOptionChange = (value) => {
    setIsRemoteAppointment(value);
    
    // Mettre à jour le contexte
    updateBookingData({
      isRemote: value
    });
    
    console.log("Option de rendez-vous à distance mise à jour:", value);
  };

  // Gérer le retour à l'étape précédente
  const handleBack = () => {
    setCurrentStep(2);
  };

  if (loading) {
    return (
      <div className="calendar-container loading-state">
        <div className="loading-spinner"></div>
        <p>Chargement du calendrier...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="calendar-container error-state">
        <div className="error-message">{error}</div>
        <button className="btn" onClick={handleBack}>
          Retour à la sélection du service
        </button>
      </div>
    );
  }

  // Déterminer si le service peut être effectué à distance
  const canBeRemote = bookingData.canBeRemote || false;
  const isRemoteOnly = bookingData.remoteOnly || false;

  return (
    <div className="calendar-container">
      <h2>Sélectionnez une date et le mode de consultation</h2>

      {/* Section pour choisir l'option de rendez-vous à distance (ajoutée ici) */}
      {canBeRemote && !isRemoteOnly && (
        <div className="remote-option-section">
          <h3>Mode de consultation</h3>
          <div className="remote-options">
            <label className={`remote-option-card ${!isRemoteAppointment ? 'selected' : ''}`}>
              <input
                type="radio"
                name="appointmentType"
                value="inPerson"
                checked={!isRemoteAppointment}
                onChange={() => handleRemoteOptionChange(false)}
              />
              <div className="option-icon">🏢</div>
              <div className="option-title">En présentiel</div>
              <div className="option-description">Rendez-vous au cabinet du praticien</div>
              <div className="option-price">{bookingData.price || 0}€</div>
            </label>
            
            <label className={`remote-option-card ${isRemoteAppointment ? 'selected' : ''}`}>
              <input
                type="radio"
                name="appointmentType"
                value="remote"
                checked={isRemoteAppointment}
                onChange={() => handleRemoteOptionChange(true)}
              />
              <div className="option-icon">💻</div>
              <div className="option-title">À distance</div>
              <div className="option-description">Consultation en ligne par vidéo</div>
              <div className="option-price">{bookingData.remotePrice || bookingData.price || 0}€</div>
            </label>
          </div>
        </div>
      )}
      
      {/* Si le service est uniquement à distance, afficher une info */}
      {isRemoteOnly && (
        <div className="remote-only-info">
          <div className="info-icon">💻</div>
          <div className="info-content">
            <h3>Consultation à distance uniquement</h3>
            <p>Ce service est disponible exclusivement en téléconsultation. Les détails de connexion vous seront envoyés après la confirmation du rendez-vous.</p>
          </div>
        </div>
      )}

      <div className="calendar-header">
        <button className="month-nav-btn" onClick={prevMonth}>&lt;</button>
        <h3>{months[currentMonth]} {currentYear}</h3>
        <button className="month-nav-btn" onClick={nextMonth}>&gt;</button>
      </div>

      <div className="calendar-weekdays">
        {weekDays.map(day => (
          <div key={day} className="weekday">{day}</div>
        ))}
      </div>

      <div className="calendar-days">
        {calendarDays.map((day, index) => {
          // Déterminer si le jour est réservable
          const bookable = isDayBookable(day);
          const isTodayDate = day.day === new Date().getDate() &&
            day.month === new Date().getMonth() &&
            day.year === new Date().getFullYear();

          // Vérifier si c'est un jour uniquement à distance
          const remoteOnly = isRemoteOnlyDay(day);

          // Déterminer le tooltip
          let tooltip = day.tooltip;
          if (!bookable && !day.isPast) {
            tooltip = "Jour non disponible";
          } else if (remoteOnly) {
            tooltip = "Uniquement à distance";
          }

          // Déterminer les classes pour le jour
          let dayClass = "calendar-day";
          if (!day.currentMonth) dayClass += " other-month";
          if (!bookable) dayClass += " non-bookable";
          if (isTodayDate) dayClass += " today";
          if (remoteOnly) dayClass += " remote-only-day";
          if (selectedDay &&
            selectedDay.day === day.day &&
            selectedDay.month === day.month &&
            selectedDay.year === day.year) {
            dayClass += " selected";
          }

          return (
            <div
              key={index}
              className={dayClass}
              onClick={() => bookable && selectDay(day)}
              title={tooltip}
              aria-disabled={!bookable}
              style={!bookable ? { pointerEvents: 'none' } : {}}
            >
              <span className="day-number">{day.day}</span>
              {remoteOnly && bookable && <span className="remote-indicator"></span>}
            </div>
          );
        })}
      </div>

      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color bookable"></span>
          <span>Disponible</span>
        </div>
        {/* <div className="legend-item">
          <span className="legend-color remote"></span>
          <span>À distance uniquement</span>
        </div> */}

        <div className="legend-item">
          <span className="legend-color non-bookable"></span>
          <span>Non disponible</span>
        </div>
      </div>

      <div className="step-navigation">
        <button className="btn btn-back" onClick={handleBack}>
          Retour
        </button>
      </div>
    </div>
  );
};

export default Calendar;